import { Download } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import SignatureGenerator from "@/components/SignatureGenerator";

const BASE = "/brand";

const ASSETS = [
  { id: "logo-dark-bg", name: "Logo for Dark Backgrounds", note: "Primary logo. White wordmark, gold caption, transparent background. Presentations, proposals, dark layouts.", file: "thrive-logo-dark-bg", light: false, square: false },
  { id: "logo-light-bg", name: "Logo for Light Backgrounds", note: "Navy wordmark on transparent. Email signatures, documents, LinkedIn.", file: "thrive-logo-light-bg", light: true, square: false },
  { id: "logo-on-navy", name: "Logo on Navy Panel", note: "Logo set on the brand navy with clear space. Social avatars, banners, cover images.", file: "thrive-logo-on-navy", light: false, square: false },
  { id: "logo-on-white", name: "Logo on White Panel", note: "Logo set on white with clear space. Print, partner listings, light media.", file: "thrive-logo-on-white", light: true, square: false },
];

function DownloadLink({ href, label, testid }) {
  return (
    <a
      href={href}
      download
      data-testid={testid}
      className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
    >
      <Download size={13} /> {label}
    </a>
  );
}

export default function Brand() {
  return (
    <div data-testid="brand-page">
      <SEO
        title="Brand Assets | Thrive Business Development"
        description="Download the Thrive Business Development logo mark and lockup in SVG and PNG for presentations, proposals, email signatures and LinkedIn."
      />

      <section className="relative overflow-hidden pt-40 pb-16 sm:pt-48 sm:pb-20" data-testid="brand-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <p className="eyebrow mb-6">Brand Assets</p>
              <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
                Thrive Logo Files
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
                Mark and lockup in SVG (vector, any size) and PNG (high resolution) for email
                signatures, LinkedIn, proposals and presentations. Please do not recolour, stretch or
                add effects to the mark.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <a
              href={`${BASE}/thrive-brand-assets.zip`}
              download
              data-testid="brand-download-all-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              <Download size={15} /> Download All (ZIP)
            </a>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="brand-assets-grid">
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {ASSETS.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.06} className="h-full">
              <div className="flex h-full flex-col bg-navy" data-testid={`brand-asset-${a.id}`}>
                <div
                  className={`flex items-center justify-center p-10 ${a.light ? "bg-[#F4F6F9]" : "bg-navy-light/60"}`}
                  style={{ minHeight: 240 }}
                >
                  <img
                    src={`${BASE}/${a.file}.svg`}
                    alt={`Thrive Business Development ${a.name}`}
                    className={a.square ? "h-32 w-32" : "w-full max-w-md"}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-5 p-8">
                  <div>
                    <h2 className="font-display text-xl font-bold tracking-[-0.03em] text-white">{a.name}</h2>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{a.note}</p>
                  </div>
                  <div className="mt-auto flex flex-wrap gap-3">
                    <DownloadLink href={`${BASE}/${a.file}.svg`} label="SVG" testid={`brand-download-${a.id}-svg`} />
                    <DownloadLink href={`${BASE}/${a.file}.png`} label="PNG" testid={`brand-download-${a.id}-png`} />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-8 border-t border-white/10 pt-10 sm:grid-cols-3" data-testid="brand-colours">
            {[
              ["Midnight Navy", "#0B0F17", "bg-navy border border-white/15"],
              ["Warm Gold", "#D4AF37", "bg-gold"],
              ["Electric Blue", "#38BDF8", "bg-electric"],
            ].map(([name, hex, cls]) => (
              <div key={hex} className="flex items-center gap-4">
                <span className={`h-12 w-12 ${cls}`} aria-hidden="true" />
                <div>
                  <p className="text-sm text-white">{name}</p>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{hex}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <SignatureGenerator />
      </section>
    </div>
  );
}
